import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useConvexSubscriptionSync } from '../hooks/useConvexPayments';
import { useConvexGitHub } from '../hooks/useConvexGitHub';
import { useAppData } from '../hooks/useAppData';
import { setGithubConnected, clearGithubError } from '../store/slices/githubSlice';
import PolarCheckout from '../components/PolarCheckout';
import SubscriptionStatus from '../components/SubscriptionStatus';
import SettingsCard from '../components/ui/SettingsCard';
import PricingCard from '../components/ui/PricingCard';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  // Use Convex subscription sync hook instead of legacy REST
  useConvexSubscriptionSync(true);
  
  // Use Convex GitHub hooks instead of REST API - FULLY MIGRATED
  const { saveGithubToken, loading: githubLoading, githubStatus } = useConvexGitHub();
  
  // Use enhanced data loading hook for proper Redux state management
  const { loadGithubStatus, syncGithubStatus } = useAppData();
  
  const dispatch = useDispatch();
  const [githubToken, setGithubToken] = useState('');
  // showUpdateForm: lets user update their token even when already connected
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  
  // Get GitHub connection state from Redux store
  const { connected: githubConnected, username } = useSelector((state) => state.github);
  const subscriptionStatus = useSelector((state) => state.subscription);

  // Real-time sync of GitHub status from Convex to Redux
  useEffect(() => {
    if (githubStatus && !githubStatus.loading) {
      dispatch(setGithubConnected({
        connected: githubStatus.connected,
        username: githubStatus.githubUsername,
        avatar_url: githubStatus.avatarUrl,
        last_validated: githubStatus.lastValidated,
        message: githubStatus.message
      }));
    }
  }, [githubStatus, dispatch]);

  /**
   * Enhanced GitHub token submission handler with complete UI update flow
   */
  const handleSaveGithubToken = async () => {
    // Clear any previous errors
    dispatch(clearGithubError());
    
    const result = await saveGithubToken(githubToken);
    
    if (result && result.success) {
      setGithubToken('');
      setShowUpdateForm(false);  // hide form after successful update
      
      setTimeout(async () => {
        await loadGithubStatus();
        syncGithubStatus();
      }, 500);
    }
  };

  const monthlyFeatures = [
    'Automated daily commits',
    'Customizable commit frequency',
    'GitHub integration',
    'Calendar dashboard',
    'Email support'
  ];

  const yearlyFeatures = [
    'Everything in Monthly',
    'Priority support',
    'Advanced analytics',
    'Early feature access'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm border border-blue-200/50 rounded-full text-sm font-medium text-blue-700 mb-4">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Account Settings
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Manage Your
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              StreakKeeper Account
            </span>
          </h1>
          <p className="text-gray-600">Manage your account, subscription, and preferences</p>
        </div>

        <div className="space-y-8">

      {/* Enhanced Subscription Plans */}
      <SettingsCard
        title="Choose Your Plan"
        description="Select a subscription plan to enable GitHub integration and automated streak maintenance"
        icon={
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        }
        gradient={true}
      >
        <div className="grid md:grid-cols-2 gap-6">
          {/* Monthly Plan */}
          <PricingCard
            title="Monthly"
            price="19"
            period="month"
            description="Perfect for trying out StreakKeeper"
            features={monthlyFeatures}
            className={subscriptionStatus.has_access && subscriptionStatus.plan_type === 'monthly' ? 'ring-2 ring-green-500' : ''}
          >
            {subscriptionStatus.has_access && subscriptionStatus.plan_type === 'monthly' ? (
              <div className="flex items-center justify-center py-4">
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  ACTIVE PLAN
                </div>
              </div>
            ) : (
              <PolarCheckout planType="monthly">
                <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-6 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5">
                  Subscribe Monthly
                </button>
              </PolarCheckout>
            )}
          </PricingCard>

          {/* Yearly Plan */}
          <PricingCard
            title="Yearly"
            price="199"
            period="year"
            description="Save $29 per year with our annual plan"
            features={yearlyFeatures}
            featured={true}
            badge={subscriptionStatus.has_access && subscriptionStatus.plan_type === 'yearly' ? "ACTIVE" : "BEST VALUE"}
            className={subscriptionStatus.has_access && subscriptionStatus.plan_type === 'yearly' ? 'ring-2 ring-green-500' : ''}
          >
            {subscriptionStatus.has_access && subscriptionStatus.plan_type === 'yearly' ? (
              <div className="flex items-center justify-center py-4">
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  ACTIVE PLAN
                </div>
              </div>
            ) : (
              <PolarCheckout planType="yearly">
                <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-6 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-green-500/25 hover:-translate-y-0.5">
                  Subscribe Yearly
                </button>
              </PolarCheckout>
            )}
          </PricingCard>
        </div>
      </SettingsCard>

      {/* Enhanced GitHub Integration with Complete UI Update Flow */}
      <SettingsCard
        title="GitHub Integration"
        description="Connect your GitHub account to enable automated streak maintenance"
        icon={
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
        }
        disabled={!subscriptionStatus.has_access}
        gradient={true}
      >
        {subscriptionStatus.has_access ? (
          <>
            {/* 🎉 SUCCESS STATE: GitHub Connected Successfully */}
            {githubConnected ? (
              <div className="space-y-4">
                {/* Connected Banner */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200/50">
                  <div className="flex items-center space-x-4">
                    {/* Success Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Success Message */}
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800 mb-1">
                        GitHub Connected Successfully!
                      </h4>
                      <p className="text-sm text-green-700">
                        Connected as: <span className="font-mono font-semibold">{username}</span>
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Your GitHub integration is active and ready for automated commits
                      </p>
                    </div>

                    {/* Update Token button — always visible when connected */}
                    {!showUpdateForm && (
                      <button
                        onClick={() => setShowUpdateForm(true)}
                        className="flex-shrink-0 text-sm text-green-700 hover:text-green-900 underline font-medium"
                      >
                        Update Token
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline update form shown when user clicks "Update Token" */}
                {showUpdateForm && (
                  <div className="space-y-4 bg-white/60 rounded-2xl border border-gray-200 p-5">
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200/50">
                      <p className="text-sm text-amber-800 font-medium">
                        Enter a new GitHub Personal Access Token to replace the current one.
                        This is required if you deleted or regenerated your token on GitHub.
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <input
                        type="password"
                        value={githubToken}
                        onChange={(e) => setGithubToken(e.target.value)}
                        placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                      />
                      <button
                        onClick={handleSaveGithubToken}
                        disabled={!githubToken || githubLoading}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 flex items-center"
                      >
                        {githubLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          'Update Token'
                        )}
                      </button>
                      <button
                        onClick={() => { setShowUpdateForm(false); setGithubToken(''); }}
                        className="px-4 py-3 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* 📝 INPUT STATE: Token Input Form (not yet connected) */
              <div className="space-y-6">
                {/* Setup Instructions */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200/50">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-3">GitHub Token Setup Instructions</h4>
                      <ol className="text-sm text-blue-800 space-y-2">
                        <li className="flex items-start">
                          <span className="font-medium mr-2">1.</span>
                          <span>Go to <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">GitHub.com → Settings → Developer settings</a></span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">2.</span>
                          <span>Click "Personal access tokens" → "Tokens (classic)"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">3.</span>
                          <span>Click "Generate new token"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">4.</span>
                          <span>Select these permissions:</span>
                        </li>
                        <li className="ml-6 space-y-1">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="font-mono text-xs">repo</span>
                            <span className="text-xs ml-2">(Full control of repositories)</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="font-mono text-xs">user:email</span>
                            <span className="text-xs ml-2">(Access email addresses)</span>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">5.</span>
                          <span>Copy the generated token and paste it below</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                {/* Token Input Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      GitHub Personal Access Token
                    </label>
                    <div className="flex space-x-3">
                      <input
                        type="password"
                        value={githubToken}
                        onChange={(e) => setGithubToken(e.target.value)}
                        placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                      />
                      <button
                        onClick={handleSaveGithubToken}
                        disabled={!githubToken || githubLoading}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 flex items-center"
                      >
                        {githubLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          'Save Token'
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200/50">
                    <div className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <div>
                        <h4 className="font-semibold text-amber-800 mb-1">Security Note</h4>
                        <p className="text-sm text-amber-700">
                          Your GitHub token is stored securely and only used for creating commits to maintain your streak. 
                          We never access your existing repositories or private data.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* 🔒 LOCKED STATE: Subscription Required */
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-gray-700 mb-3">GitHub Integration Locked</h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Subscribe to a plan to enable GitHub integration and automated streak maintenance.
            </p>
            <div className="flex justify-center space-x-4">
              <PolarCheckout planType="monthly">
                <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5">
                  Subscribe Monthly
                </button>
              </PolarCheckout>
              <PolarCheckout planType="yearly">
                <button className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-green-500/25 hover:-translate-y-0.5">
                  Subscribe Yearly
                </button>
              </PolarCheckout>
            </div>
          </div>
        )}
      </SettingsCard>

      {/* Enhanced Subscription Status - Only show if user has an active subscription */}
      {subscriptionStatus.has_access && (
        <SettingsCard
          title="Account Status"
          description="View your current subscription details and billing information"
          icon={
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          }
          gradient={true}
        >
          <SubscriptionStatus />
        </SettingsCard>
      )}
    </div>
  </div>
</div>
  );
};

export default SettingsPage;